import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ShieldCheck, Zap, XCircle, Edit3, Download, Send, Check, Loader,
  RefreshCcw, FileText, Share2, Clock, ChevronRight,
  Lock, Server, Activity, EyeOff, Cpu, AlertTriangle,
  Inbox, FileCheck,
} from 'lucide-react';
import styles from './LandingPage.module.css';

const Nav = () => (
  <nav className={styles.nav}>
    <div className={styles['nav-inner']}>
      <Link to="/" className={styles['nav-brand']}>
        <span className={styles['nav-mark']}>AB</span>
        <span className={styles['nav-wordmark']}>AppealBot</span>
        <span className={styles['nav-by']}>by Meduri</span>
      </Link>
      <div className={styles['nav-links']}>
        <a href="#product">Product</a>
        <a href="#how">How it works</a>
        <a href="#trust">Security</a>
        <a href="#pricing">Pricing</a>
        <a href="#">Docs</a>
      </div>
      <div className={styles['nav-right']}>
        <Link to="/login" className={styles.btn} style={{ border: 'none', padding: '0 6px' }}>
          Sign in
        </Link>
        <Link to="/signup" className={`${styles.btn} ${styles.primary}`}>
          Start free trial
        </Link>
      </div>
    </div>
  </nav>
);

const Hero = () => (
  <header className={styles.hero}>
    <div className={styles.container}>
      <div className={styles['hero-grid']}>
        <div className={styles['hero-copy']}>
          <div className={styles['hero-tag']}>
            <span className={styles['hero-tag-pill']}>NEW</span>
            <span>Built for physical therapy &amp; small specialty clinics</span>
          </div>
          <h1 className={styles['h-display']}>
            Stop writing off <em>denied claims</em>.
          </h1>
          <p className={styles['hero-sub']}>
            AppealBot reads your denial letters and ERAs, drafts the appeal and letter of medical
            necessity, and tracks every submission — so the claims you'd normally walk away from
            get paid instead.
          </p>
          <div className={styles['hero-ctas']}>
            <Link to="/signup" className={`${styles.btn} ${styles.primary}`}>
              Start free trial <ArrowRight size={16} />
            </Link>
            <a href="#how" className={styles.btn}>
              See how it works
            </a>
          </div>
          <div className={styles['hero-meta']}>
            <span><ShieldCheck size={14} /> HIPAA-conscious design</span>
            <span><Zap size={14} /> Setup in under 15 minutes</span>
            <span><XCircle size={14} /> No billing software required</span>
          </div>
        </div>

        <div className={styles['hero-frame']}>
          <div className={styles['win-bar']}>
            <span className={styles['win-dot']} />
            <span className={styles['win-dot']} />
            <span className={styles['win-dot']} />
            <span className={styles['win-title']}>app.appealbot.com/appeals/WC9987712</span>
            <span style={{ fontSize: '11px', color: '#64748B' }}>Draft</span>
          </div>
          <div className={styles['mock-shell']}>
            <aside className={styles['mock-side']}>
              <div className={styles.brand}>
                <span className={styles.mark}>AB</span>
                <span className={styles.wm}>AppealBot</span>
              </div>
              <div className={styles['nav-item']}>Overview</div>
              <div className={styles['nav-item']}>Claims</div>
              <div className={`${styles['nav-item']} ${styles.active}`}>Appeals</div>
              <div className={styles['nav-item']}>Denial Analysis</div>
              <div className={styles['nav-item']}>PT Notes</div>
              <div className={`${styles['nav-item']} ${styles.sub}`} style={{ marginTop: 'auto' }}>Documents</div>
              <div className={`${styles['nav-item']} ${styles.sub}`}>Billing</div>
            </aside>
            <div className={styles['mock-main']}>
              <div className={styles['mock-head']}>
                <div>
                  <h2>Appeal · WC9987712</h2>
                  <div className={styles.sub}>Maria García · BCBS · $612.50 · Denial CO-50</div>
                </div>
                <span className={`${styles['mock-pill']} ${styles.draft}`}>
                  <Edit3 size={10} /> Draft
                </span>
              </div>
              <div className={styles['stat-row']}>
                <div className={styles['stat-card']}>
                  <div className={styles.v}>CO-50</div>
                  <div className={styles.l}>Denial Code</div>
                  <div className={styles.s}>Non-covered service</div>
                </div>
                <div className={`${styles['stat-card']} ${styles.warn}`}>
                  <div className={styles.v}>3</div>
                  <div className={styles.l}>Docs Attached</div>
                  <div className={styles.s}>ERA, note, referral</div>
                </div>
                <div className={`${styles['stat-card']} ${styles.success}`}>
                  <div className={styles.v}>98%</div>
                  <div className={styles.l}>Match Confidence</div>
                  <div className={styles.s}>AI draft quality</div>
                </div>
              </div>
              <div style={{ border: '1px solid #000', padding: '14px', background: '#F8F9FC' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748B', marginBottom: '8px' }}>
                  Draft letter
                </div>
                <div style={{ fontSize: '11.5px', lineHeight: 1.6, color: '#1D2433' }}>
                  To Whom It May Concern,<br /><br />
                  I am writing to appeal the denial of claim <strong>WC9987712</strong> for{' '}
                  <strong>Maria García</strong>, date of service <strong>12/30/25</strong>. The
                  denial code <strong>CO-50</strong> indicates the service is deemed not medically
                  necessary, however <mark>the procedure performed (97110) is supported by the
                  attached plan of care and progress notes</mark> documenting functional deficits
                  in strength and ROM.<br /><br />
                  Please reconsider this claim and remit payment at the contracted rate.
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                <button className={styles['mini-btn']}><Edit3 size={12} /> Edit</button>
                <button className={styles['mini-btn']}><Download size={12} /> Export PDF</button>
                <button className={`${styles['mini-btn']} ${styles.primary}`} style={{ marginLeft: 'auto' }}>
                  <Send size={12} /> Send via Fax
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div style={{ height: '96px' }} />
  </header>
);

const PayerRow = () => (
  <div className={styles['logo-row']}>
    <div className={styles['logo-row-inner']}>
      <div className={styles['logo-row-label']}>Works with claims from</div>
      <div className={styles['payer-chips']}>
        <span className={styles['payer-chip']}>Aetna</span>
        <span className={styles['payer-chip']}>BCBS</span>
        <span className={styles['payer-chip']}>UnitedHealthcare</span>
        <span className={styles['payer-chip']}>Cigna</span>
        <span className={styles['payer-chip']}>Humana</span>
        <span className={styles['payer-chip']}>Medicare</span>
        <span className={styles['payer-chip']}>Medicaid</span>
      </div>
    </div>
  </div>
);

const ProblemSection = () => (
  <section className={styles.section} id="problem">
    <div className={styles.container}>
      <div className={styles['problem-grid']}>
        <div>
          <span className={styles.eyebrow}>The problem</span>
          <h2 className={styles['h-section']}>Denied claims quietly become written-off revenue.</h2>
          <p className={styles.lead} style={{ marginTop: '20px' }}>
            Appealing a single claim takes a billing team 45 minutes of pulling EOBs, hunting
            denial codes, drafting a letter, attaching documentation, and faxing it to the payer.
            Small practices don't have 45 minutes. So most denials are never appealed.
          </p>

          <div className={styles['problem-list']}>
            <div className={styles['problem-item']}>
              <div className={styles['problem-num']}>01</div>
              <div>
                <h4 className={styles['problem-title']}>One billing specialist, hundreds of claims a month</h4>
                <p className={styles['problem-body']}>Most PT clinics staff a single biller. Between eligibility checks, coding, and posting payments, there is no remaining capacity to appeal a denial line-by-line.</p>
              </div>
              <span className={`${styles['problem-quant']} ${styles.time}`}>~45 min / appeal</span>
            </div>
            <div className={styles['problem-item']}>
              <div className={styles['problem-num']}>02</div>
              <div>
                <h4 className={styles['problem-title']}>CO-50, CO-97, CO-45 — the same denials, every week</h4>
                <p className={styles['problem-body']}>The same three or four denial codes drive most denials. Each one requires a slightly different argument and a slightly different piece of documentation. It is pattern-matching work, done from scratch.</p>
              </div>
              <span className={styles['problem-quant']}>15–30% denial rate</span>
            </div>
            <div className={styles['problem-item']}>
              <div className={styles['problem-num']}>03</div>
              <div>
                <h4 className={styles['problem-title']}>No tracking. No deadlines. No outcomes.</h4>
                <p className={styles['problem-body']}>Appeals get sent into a fax black hole. When the payer's 180-day window closes, the revenue is gone — and nobody knows which appeals worked, which didn't, and why.</p>
              </div>
              <span className={styles['problem-quant']}>180-day window</span>
            </div>
            <div className={styles['problem-item']}>
              <div className={styles['problem-num']}>04</div>
              <div>
                <h4 className={styles['problem-title']}>Six figures walk out the door every year</h4>
                <p className={styles['problem-body']}>A mid-sized PT clinic typically leaves $80K–$150K in recoverable revenue on the table annually — not because the claims were unwinnable, but because nobody had time to appeal them.</p>
              </div>
              <span className={styles['problem-quant']}>Lost revenue</span>
            </div>
          </div>
        </div>

        <div className={styles['problem-visual']}>
          <h4>Sample month — one 6-provider clinic</h4>
          <div className={styles.ledger}>
            <div className={styles['ledger-row']}>
              <span className={styles['ledger-label']}>CO-50 · Not medically necessary</span>
              <span className={styles['ledger-amt']}>24 claims</span>
              <span className={styles['ledger-amt']}>$14,710</span>
            </div>
            <div className={styles['ledger-row']}>
              <span className={styles['ledger-label']}>CO-97 · Bundled / already paid</span>
              <span className={styles['ledger-amt']}>18 claims</span>
              <span className={styles['ledger-amt']}>$8,420</span>
            </div>
            <div className={styles['ledger-row']}>
              <span className={styles['ledger-label']}>CO-16 · Missing information</span>
              <span className={styles['ledger-amt']}>11 claims</span>
              <span className={styles['ledger-amt']}>$3,985</span>
            </div>
            <div className={styles['ledger-row']}>
              <span className={styles['ledger-label']}>CO-45 · Above allowed amount</span>
              <span className={styles['ledger-amt']}>9 claims</span>
              <span className={styles['ledger-amt']}>$1,220</span>
            </div>
            <div className={styles['ledger-row']}>
              <span className={styles['ledger-label']}>Other denial reasons</span>
              <span className={styles['ledger-amt']}>7 claims</span>
              <span className={styles['ledger-amt']}>$2,140</span>
            </div>
            <div className={styles['ledger-total']}>
              <span>Denied, unappealed</span>
              <span className={styles.n}>$30,475</span>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: '#64748B', margin: '20px 0 0', lineHeight: 1.5 }}>
            Representative example based on aggregated denial data from 6-provider outpatient PT
            clinics. Your numbers will vary by payer mix and coding practice.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const FeatureUpload = () => (
  <article className={styles['feat-row']}>
    <div className={styles['feat-copy']}>
      <div className={styles['feat-step']}><span className={styles['feat-step-num']}>1</span> Intake</div>
      <h3 className={styles['feat-title']}>Upload the denial. We extract the codes.</h3>
      <p className={styles['feat-desc']}>
        Drop in the denial letter, ERA 835, or paper EOB. AppealBot OCRs the document, pulls the
        CARC/RARC codes, patient, claim number, DOS, and billed/denied amounts into a structured
        record.
      </p>
      <ul className={styles['feat-bullets']}>
        <li>PDF, image, ERA 835, or manual entry</li>
        <li>Works from payer portals, clearinghouse exports, or scanned mail</li>
        <li>Extracted fields are always editable — AI is a starting point, not the final word</li>
      </ul>
    </div>
    <div className={styles['feat-media']}>
      <div className={`${styles['feat-media-inner']} ${styles['mock-upload']}`}>
        <div className={`${styles['doc-row']} ${styles.parsed}`}>
          <div className={styles['doc-icon']}>ERA<br />835</div>
          <div>
            <p className={styles['doc-title']}>BCBS_ERA_2025-12-30.pdf</p>
            <div className={styles['doc-meta']}>
              <span>1.2 MB</span>
              <span className={styles.dot}>·</span>
              <span>Parsed in 3.4s</span>
              <span className={styles.dot}>·</span>
              <span style={{ color: '#1F8A4C', fontWeight: 600 }}>6 claims found</span>
            </div>
          </div>
          <span className={`${styles['mock-pill']} ${styles.approved}`}><Check size={10} /> Ready</span>
        </div>
        <div className={styles['doc-row']}>
          <div className={styles['doc-icon']}>EOB<br />PDF</div>
          <div>
            <p className={styles['doc-title']}>Aetna_denial_WC1234567.pdf</p>
            <div className={styles['doc-meta']}>
              <span>612 KB</span>
              <span className={styles.dot}>·</span>
              <span>Parsing…</span>
            </div>
          </div>
          <span className={`${styles['mock-pill']} ${styles.pending}`}><Loader size={10} /> Working</span>
        </div>

        <div className={styles['ex-box']}>
          <div className={styles['ex-title']}>Extracted · WC9987712</div>
          <div className={styles['ex-grid']}>
            <div className={styles['ex-field']}><span className={styles.lab}>Patient</span><span className={styles.val}>Maria García</span></div>
            <div className={styles['ex-field']}><span className={styles.lab}>Payer</span><span className={styles.val}>BCBS PPO</span></div>
            <div className={styles['ex-field']}><span className={styles.lab}>Date of service</span><span className={styles.val}>12/30/2025</span></div>
            <div className={styles['ex-field']}><span className={styles.lab}>Billed amount</span><span className={styles.val}>$612.50</span></div>
            <div className={styles['ex-field']}><span className={styles.lab}>CPT code</span><span className={styles.val}>97110</span></div>
            <div className={styles['ex-field']}><span className={styles.lab}>Denial code</span><span className={`${styles.val} ${styles.red}`}>CO-50</span></div>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const FeatureLetter = () => (
  <article className={`${styles['feat-row']} ${styles.reverse}`}>
    <div className={styles['feat-copy']}>
      <div className={styles['feat-step']}><span className={styles['feat-step-num']}>2</span> Drafting</div>
      <h3 className={styles['feat-title']}>AI writes the appeal letter — and the LMN.</h3>
      <p className={styles['feat-desc']}>
        For each denial code, AppealBot generates the appeal letter and, when needed, a letter of
        medical necessity grounded in your patient's own notes and plan of care. The argument is
        specific to the code, not a template.
      </p>
      <ul className={styles['feat-bullets']}>
        <li>Denial-code-specific reasoning (CO-50 cites medical necessity, CO-97 cites separately identifiable service)</li>
        <li>Pulls evidence from your uploaded PT notes, referral, and plan of care</li>
        <li>Citations are highlighted — every quoted line links back to its source document</li>
      </ul>
    </div>
    <div className={styles['feat-media']}>
      <div className={`${styles['feat-media-inner']} ${styles['mock-letter']}`}>
        <div className={styles['letter-panel']}>
          <div className={styles.ph}>
            <span>Letter · Draft v2</span>
            <span style={{ color: '#1F8A4C' }}>● Auto-saved</span>
          </div>
          <div className={styles['letter-body']}>
            <p>To the BCBS Appeals Department,</p>
            <p>I am writing to appeal the denial of claim <strong>WC9987712</strong> for{' '}
              <strong>Maria García</strong>, DOS 12/30/2025, denied under <strong>CO-50</strong>.</p>
            <p><mark>The procedure billed (CPT 97110, therapeutic exercise) was medically necessary
              per the attached plan of care dated 11/15/2025</mark>, which documents a 40° deficit
              in shoulder flexion and a goal of restoring functional reach by week 8.</p>
            <p><span className={styles.redline}>Please find enclosed the insurance card and ID.</span>{' '}
              Per the progress note of 12/30/2025, the patient demonstrated measurable gains under
              this plan.</p>
            <p>We respectfully request reconsideration and payment at the contracted rate.</p>
          </div>
          <div className={styles['letter-controls']}>
            <button className={styles['mini-btn']}><Edit3 size={12} /> Edit</button>
            <button className={styles['mini-btn']}><RefreshCcw size={12} /> Regenerate</button>
            <button className={styles['mini-btn']}><FileText size={12} /> Add LMN</button>
          </div>
        </div>
        <div className={styles['letter-meta']}>
          <div className={styles['meta-card']}>
            <div className={styles.t}>Evidence cited</div>
            <div className={styles['meta-line']}><span className={styles.k}>Plan of Care</span><span className={styles.v}>11/15/25</span></div>
            <div className={styles['meta-line']}><span className={styles.k}>Progress note</span><span className={styles.v}>12/30/25</span></div>
            <div className={styles['meta-line']}><span className={styles.k}>Referral</span><span className={styles.v}>Dr. Ramos</span></div>
          </div>
          <div className={styles['meta-card']}>
            <div className={styles.t}>Appeal strategy</div>
            <div style={{ fontSize: '11.5px', lineHeight: 1.55, color: '#1D2433' }}>
              CO-50 requires demonstration of medical necessity. Letter cites functional deficit,
              plan of care, and documented progress — the three elements BCBS looks for in this code.
            </div>
          </div>
          <div className={styles['meta-card']} style={{ background: '#F8F9FC' }}>
            <div className={styles.t}>Match confidence</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '24px', fontWeight: 800, color: '#1F8A4C' }}>98%</span>
              <span style={{ fontSize: '11px', color: '#64748B' }}>High · similar appeals won for this payer</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const FeatureSubmit = () => (
  <article className={styles['feat-row']}>
    <div className={styles['feat-copy']}>
      <div className={styles['feat-step']}><span className={styles['feat-step-num']}>3</span> Submission</div>
      <h3 className={styles['feat-title']}>Send by fax, export, or your clearinghouse.</h3>
      <p className={styles['feat-desc']}>
        Most payers still want a fax. AppealBot faxes directly on your behalf, or exports a
        print-ready PDF, or drops the packet into your clearinghouse workflow. You pick per appeal.
      </p>
      <ul className={styles['feat-bullets']}>
        <li>Built-in HIPAA-compliant fax to the payer's correct appeals number</li>
        <li>Print-ready PDF export with all attachments collated</li>
        <li>Clearinghouse integrations for Availity, Waystar, and Office Ally</li>
      </ul>
    </div>
    <div className={styles['feat-media']}>
      <div className={`${styles['feat-media-inner']} ${styles['mock-send']}`}>
        <div className={styles['send-title']}>Submit appeal · WC9987712</div>
        <div className={styles['send-grid']}>
          <div className={`${styles['send-option']} ${styles.selected}`}>
            <div className={styles.ico}><Send size={14} /></div>
            <div className={styles.ttl}>Fax to payer</div>
            <div className={styles.sub}>1-800-555-4421 · BCBS Appeals</div>
            <div className={styles.rad} />
          </div>
          <div className={styles['send-option']}>
            <div className={styles.ico}><Download size={14} /></div>
            <div className={styles.ttl}>Export PDF</div>
            <div className={styles.sub}>Print &amp; mail yourself</div>
            <div className={styles.rad} />
          </div>
          <div className={styles['send-option']}>
            <div className={styles.ico}><Share2 size={14} /></div>
            <div className={styles.ttl}>Clearinghouse</div>
            <div className={styles.sub}>Availity · connected</div>
            <div className={styles.rad} />
          </div>
        </div>
        <div className={styles['send-log']}>
          <div className={styles['log-title']}>Submission log</div>
          <div className={styles['log-row']}><span className={styles.ts}>10:42:11</span><span className={styles.ev}>Letter generated</span><span className={styles.st}>OK</span></div>
          <div className={styles['log-row']}><span className={styles.ts}>10:43:02</span><span className={styles.ev}>User reviewed &amp; edited draft</span><span className={styles.st}>OK</span></div>
          <div className={styles['log-row']}><span className={styles.ts}>10:43:28</span><span className={styles.ev}>3 documents attached</span><span className={styles.st}>OK</span></div>
          <div className={styles['log-row']}><span className={styles.ts}>10:43:44</span><span className={styles.ev}>Fax transmitted to 1-800-555-4421</span><span className={`${styles.st} ${styles.pending}`}>SENDING</span></div>
        </div>
      </div>
    </div>
  </article>
);

const FeatureTrack = () => (
  <article className={`${styles['feat-row']} ${styles.reverse}`}>
    <div className={styles['feat-copy']}>
      <div className={styles['feat-step']}><span className={styles['feat-step-num']}>4</span> Tracking</div>
      <h3 className={styles['feat-title']}>Every appeal visible. Every deadline tracked.</h3>
      <p className={styles['feat-desc']}>
        Watch each appeal through draft, submitted, received, and resolved. AppealBot shows which
        ones are close to the payer's appeal-window deadline and which payers are reversing your
        denials.
      </p>
      <ul className={styles['feat-bullets']}>
        <li>Countdown on each appeal against the payer's 180-day (or custom) window</li>
        <li>Outcome tracking — approved, partially paid, upheld denial</li>
        <li>Per-payer and per-denial-code win rates so you learn what arguments work</li>
      </ul>
    </div>
    <div className={styles['feat-media']}>
      <div className={`${styles['feat-media-inner']} ${styles['mock-track']}`}>
        <div className={styles['track-head']}>
          <h3>All Appeals</h3>
          <span className={styles.rt}>Updated just now</span>
        </div>
        <div className={styles['stat-row']} style={{ marginBottom: '14px' }}>
          <div className={`${styles['stat-card']} ${styles.warn}`}>
            <div className={styles.v}>3</div><div className={styles.l}>Needs Attention</div><div className={styles.s}>Drafts to complete</div>
          </div>
          <div className={styles['stat-card']}>
            <div className={styles.v}>12</div><div className={styles.l}>Total Appeals</div><div className={styles.s}>All time</div>
          </div>
          <div className={`${styles['stat-card']} ${styles.success}`}>
            <div className={styles.v}>9</div><div className={styles.l}>Submitted</div><div className={styles.s}>Successfully sent</div>
          </div>
        </div>
        <div className={styles['track-table']}>
          <table>
            <thead>
              <tr><th>Status</th><th>Claim</th><th>Patient</th><th>Payer</th><th>Code</th><th>Deadline</th></tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ color: '#1F8A4C', fontWeight: 600 }}>Approved</td>
                <td className={styles['claim-link']}>WC4401223</td><td>Chloe Ahmed</td><td>Aetna</td><td>—</td><td>—</td>
              </tr>
              <tr className={styles.alt}>
                <td style={{ color: '#1D4ED8', fontWeight: 600 }}>Submitted</td>
                <td className={styles['claim-link']}>WC9987712</td><td>Maria García</td><td>BCBS</td><td>CO-50</td><td style={{ color: '#64748B' }}>146 days</td>
              </tr>
              <tr>
                <td style={{ color: '#B7791F', fontWeight: 600 }}>Pending</td>
                <td className={styles['claim-link']}>WC5520031</td><td>Aaron Park</td><td>United</td><td>CO-16</td><td style={{ color: '#64748B' }}>168 days</td>
              </tr>
              <tr className={styles.alt}>
                <td style={{ color: '#FF0004', fontWeight: 600 }}>Draft</td>
                <td className={styles['claim-link']}>WC7811104</td><td>Lia Nakamura</td><td>Cigna</td><td>CO-45</td><td style={{ color: '#B7791F', fontWeight: 600 }}>11 days</td>
              </tr>
              <tr>
                <td style={{ color: '#FF0004', fontWeight: 600 }}>Draft</td>
                <td className={styles['claim-link']}>WC3320045</td><td>Sam Obarski</td><td>United</td><td>CO-97</td><td style={{ color: '#B7791F', fontWeight: 600 }}>4 days</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </article>
);

const FeaturesSection = () => (
  <section className={styles.section} id="product" style={{ background: '#F8F9FC' }}>
    <div className={styles.container}>
      <div className={styles['feat-head']}>
        <span className={styles.eyebrow}>The product</span>
        <h2 className={styles['h-section']}>Four steps. One afternoon's backlog, cleared.</h2>
        <p className={styles.lead} style={{ marginTop: '20px' }}>
          AppealBot is a focused, four-step workflow — not a platform, not a suite. Upload the
          denial, let the AI extract and draft, review the letter, then submit via fax or export.
          Every step stays on-screen and editable.
        </p>
      </div>

      <div className={styles['feat-stack']}>
        <FeatureUpload />
        <FeatureLetter />
        <FeatureSubmit />
        <FeatureTrack />
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className={styles.section} id="how">
    <div className={styles.container}>
      <span className={styles.eyebrow}>How it works</span>
      <h2 className={styles['h-section']}>From denial to fax — in about four minutes.</h2>
      <p className={styles.lead} style={{ marginTop: '20px' }}>A typical appeal, end-to-end.</p>

      <div className={styles['hw-steps']}>
        <div className={styles['hw-step']}>
          <div className={styles['hw-num']}>STEP 01</div>
          <h3>Upload denial</h3>
          <p>Drop in the ERA 835, payer portal PDF, or a scanned EOB. Or pull it straight from your clearinghouse connection.</p>
          <div className={styles.time}><Clock size={12} /> ~20 seconds</div>
          <div className={styles['hw-arrow']}><ChevronRight size={10} /></div>
        </div>
        <div className={styles['hw-step']}>
          <div className={styles['hw-num']}>STEP 02</div>
          <h3>Generate letter</h3>
          <p>AppealBot selects the right argument for the denial code and cites evidence from the patient's notes and plan of care.</p>
          <div className={styles.time}><Clock size={12} /> ~30 seconds</div>
          <div className={styles['hw-arrow']}><ChevronRight size={10} /></div>
        </div>
        <div className={styles['hw-step']}>
          <div className={styles['hw-num']}>STEP 03</div>
          <h3>Review &amp; edit</h3>
          <p>Inline edit any sentence. Regenerate a paragraph. Swap in a different piece of evidence. You stay in control of what gets sent.</p>
          <div className={styles.time}><Clock size={12} /> ~2–3 minutes</div>
          <div className={styles['hw-arrow']}><ChevronRight size={10} /></div>
        </div>
        <div className={styles['hw-step']}>
          <div className={styles['hw-num']}>STEP 04</div>
          <h3>Submit &amp; track</h3>
          <p>Fax from AppealBot, export a PDF, or push to your clearinghouse. Status and deadline are tracked automatically.</p>
          <div className={styles.time}><Clock size={12} /> ~30 seconds</div>
        </div>
      </div>
    </div>
  </section>
);

const BuiltForSmallPractices = () => (
  <section className={styles['small-wrap']}>
    <div className={styles.container}>
      <div className={styles['small-grid']}>
        <div>
          <span className={styles.eyebrow}>Built for small practices</span>
          <h2 className={styles['h-section']}>Not an enterprise RCM platform. On purpose.</h2>
          <p className={styles.lead} style={{ marginTop: '20px' }}>
            Enterprise denial management tools assume a 20-person billing department, a six-month
            rollout, and a five-figure implementation fee. AppealBot assumes you have one person,
            one computer, and a stack of denials.
          </p>

          <div className={styles['not-for']}>
            <strong>AppealBot is not</strong> a full revenue-cycle platform, a billing system, or
            an EHR. It does one thing: turn denied claims into sent appeals. If you already have
            billing software, AppealBot sits next to it.
          </div>
        </div>

        <div className={styles['small-bullets']}>
          <div className={styles['small-item']}>
            <div className={styles.n}>01</div>
            <div>
              <h4>No integration required to start</h4>
              <p>Upload a denial letter on day one. Clearinghouse and EHR integrations are optional and can be added later when you're ready.</p>
            </div>
          </div>
          <div className={styles['small-item']}>
            <div className={styles.n}>02</div>
            <div>
              <h4>One seat, one price, one workflow</h4>
              <p>No user-based pricing tiers. No admin console. Your biller signs in and starts appealing — that's the entire onboarding.</p>
            </div>
          </div>
          <div className={styles['small-item']}>
            <div className={styles.n}>03</div>
            <div>
              <h4>Under 15 minutes from signup to first appeal</h4>
              <p>Create account, upload a denial, generate the letter, review, send. We've timed it with real clinics.</p>
            </div>
          </div>
          <div className={styles['small-item']}>
            <div className={styles.n}>04</div>
            <div>
              <h4>No new login for your staff</h4>
              <p>Single sign-on with Google Workspace or Microsoft 365 — the accounts your clinic already uses.</p>
            </div>
          </div>
          <div className={styles['small-item']}>
            <div className={styles.n}>05</div>
            <div>
              <h4>Human support, not a ticket queue</h4>
              <p>Email and Loom-video replies from people who actually know the product. No tier-one outsourced support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TrustSection = () => (
  <section className={styles.section} id="trust">
    <div className={styles.container}>
      <span className={styles.eyebrow}>Security &amp; compliance</span>
      <h2 className={styles['h-section']}>HIPAA-conscious by design, transparent by default.</h2>
      <p className={styles.lead} style={{ marginTop: '20px', marginBottom: '48px' }}>
        We won't claim certifications we haven't earned. Here's what we do today, documented and inspectable.
      </p>

      <div className={styles['trust-grid']}>
        <div className={styles['trust-card']}>
          <h3 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.015em', margin: 0 }}>
            What we sign &amp; how we handle PHI
          </h3>

          <div className={styles['trust-badges']}>
            <span className={styles['trust-badge']}><ShieldCheck size={14} className={styles.ico} /> BAA on request</span>
            <span className={styles['trust-badge']}><Lock size={14} className={styles.ico} /> AES-256 at rest</span>
            <span className={styles['trust-badge']}><Server size={14} className={styles.ico} /> US-only hosting</span>
            <span className={styles['trust-badge']}><Activity size={14} className={styles.ico} /> SOC 2 Type 1 · in progress</span>
          </div>

          <div className={styles['trust-list']}>
            <div className={styles['trust-list-item']}>
              <FileText size={14} />
              <div>
                <strong>Business Associate Agreement</strong>
                <p>Signed BAA available before any PHI touches the platform. Same standard language insurers and clearinghouses use.</p>
              </div>
            </div>
            <div className={styles['trust-list-item']}>
              <EyeOff size={14} />
              <div>
                <strong>PHI minimization</strong>
                <p>Only the fields needed to draft an appeal are stored. Source documents are encrypted, scoped to your account, and deletable on request.</p>
              </div>
            </div>
            <div className={styles['trust-list-item']}>
              <Cpu size={14} />
              <div>
                <strong>No training on your data</strong>
                <p>Your uploaded denials, notes, and letters are never used to train underlying models. Each draft is generated under a zero-retention contract with our LLM provider.</p>
              </div>
            </div>
            <div className={styles['trust-list-item']}>
              <AlertTriangle size={14} />
              <div>
                <strong>Incident disclosure</strong>
                <p>A public status page and a 72-hour notification commitment for any security incident affecting your account — written into the BAA.</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles['trust-card']} style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '32px 32px 0' }}>
            <h3 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.015em', margin: '0 0 8px' }}>
              Every action is logged.
            </h3>
            <p style={{ fontSize: '14px', color: '#1D2433', lineHeight: 1.55, margin: '0 0 20px' }}>
              Every upload, draft generation, edit, and fax transmission is recorded with user,
              timestamp, and affected record. Your compliance officer can export the audit log any time.
            </p>
          </div>

          <div className={styles['audit-log']}>
            <div className={styles['al-title']}>Audit log · WC9987712</div>
            <div className={styles['al-row']}><span className={styles.ts}>10:42:11</span><span className={styles.ev}><span className={styles.u}>amelia@clinic.com</span> uploaded BCBS_ERA_2025-12-30.pdf</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:42:14</span><span className={styles.ev}>system parsed 6 claims · CO-50 × 2, CO-97 × 1, CO-16 × 3</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:42:58</span><span className={styles.ev}><span className={styles.u}>amelia@clinic.com</span> generated appeal letter v1</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:43:21</span><span className={styles.ev}><span className={styles.u}>amelia@clinic.com</span> edited paragraph 3 · 12 chars changed</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:43:44</span><span className={styles.ev}><span className={styles.u}>amelia@clinic.com</span> transmitted fax to 1-800-555-4421</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:43:49</span><span className={styles.ev}>payer fax receipt confirmed · 4 pages</span></div>
            <div className={styles['al-row']}><span className={styles.ts}>10:44:02</span><span className={styles.ev}>appeal status set to Submitted</span></div>
          </div>

          <div style={{ padding: '20px 32px', borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: '12px', color: '#64748B' }}>
            <Download size={12} style={{ verticalAlign: '-2px' }} /> Export the full log as CSV · retention controlled by you
          </div>
        </div>
      </div>
    </div>
  </section>
);

const RoiSection = () => (
  <section className={styles.section} id="roi" style={{ background: '#F8F9FC' }}>
    <div className={styles.container}>
      <div className={styles['roi-head']}>
        <span className={styles.eyebrow}>What changes</span>
        <h2 className={styles['h-section']}>Less time spent. Revenue recovered. No fake numbers.</h2>
        <p className={styles.lead} style={{ marginTop: '20px' }}>
          We're early. We're not going to slap a "500% ROI" stamp on this page. Here's what the
          first clinics using AppealBot have actually told us.
        </p>
      </div>

      <div className={styles['roi-grid']}>
        <div className={styles['roi-cell']}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Clock size={20} />
            <h3>Time per appeal</h3>
          </div>
          <div className={styles.big}>
            ~45<span style={{ fontSize: '32px', letterSpacing: '-0.01em' }}> → </span>4{' '}
            <span style={{ fontSize: '28px', fontWeight: 500, color: '#64748B' }}>min</span>
          </div>
          <p>The minutes you'd normally spend hunting the denial code and typing a letter become a three-click review of a draft.</p>
        </div>
        <div className={styles['roi-cell']}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Inbox size={20} />
            <h3>Appeal rate</h3>
          </div>
          <div className={styles.big}>More<br />appeals.</div>
          <p>Clinics who used to appeal only their highest-dollar denials start appealing the $180 ones too — because it's no longer 45 minutes of work to do so.</p>
        </div>
        <div className={styles['roi-cell']}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileCheck size={20} />
            <h3>Recovered revenue</h3>
          </div>
          <div className={styles.big}>
            Qualitative.<br />
            <span style={{ fontSize: '17px', color: '#64748B', fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.3, display: 'block', marginTop: '8px' }}>
              Your numbers = your payer mix.
            </span>
          </div>
          <p>How much you recover depends on your denial rate, payer mix, and codes — we'll show you your actual numbers after 30 days of use, not a made-up figure before.</p>
        </div>
      </div>

      <p className={styles['roi-caveat']}>
        <strong>We don't publish benchmark numbers yet.</strong> Every practice's payer mix and
        denial pattern is different, and we'd rather earn your trust with the data from your own
        account than with a made-up industry average.
      </p>
    </div>
  </section>
);

const Pricing = () => (
  <section className={styles['price-wrap']} id="pricing">
    <div className={styles.container}>
      <div className={styles['price-head']}>
        <span className={styles.eyebrow} style={{ display: 'block', textAlign: 'left' }}>Pricing &amp; access</span>
        <h2 className={styles['h-section']}>Honest early-stage pricing.</h2>
        <p className={styles.lead}>
          Try it for free on your first ten denials. After that, pay per appeal you send — or take
          a monthly plan if you're ready to move your backlog.
        </p>
      </div>

      <div className={styles['price-grid']} style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: '780px' }}>
        <div className={styles['price-col']}>
          <div className={styles['price-label']}>Free trial</div>
          <h3 className={styles['price-name']}>Pilot</h3>
          <div className={styles['price-cost']}><span className={styles.n}>$0</span></div>
          <div className={styles['price-sub']}>Your first 10 appeals, on us. No credit card, no clearinghouse setup, no call with sales.</div>
          <ul>
            <li>10 free generated appeal letters</li>
            <li>Upload denial letters &amp; ERAs</li>
            <li>Export PDF or send via fax</li>
            <li>Email support</li>
          </ul>
          <Link to="/signup" className={styles.btn}>Start pilot</Link>
        </div>
        <div className={`${styles['price-col']} ${styles.featured}`}>
          <div className={styles['price-label']}>Most popular</div>
          <h3 className={styles['price-name']}>Practice</h3>
          <div className={styles['price-cost']}><span className={styles.n}>$29.99</span><span className={styles.u}>/ month</span></div>
          <div className={styles['price-sub']}>Unlimited appeals for your clinic. A flat monthly fee — no per-appeal charges, no surprise bills.</div>
          <ul>
            <li>Everything in Pilot</li>
            <li>Unlimited generated appeals</li>
            <li>HIPAA-compliant fax included</li>
            <li>Outcome &amp; deadline tracking</li>
            <li>Letter of medical necessity generator</li>
            <li>BAA included</li>
          </ul>
          <Link to="/signup" className={`${styles.btn} ${styles.primary}`} style={{ background: '#fff', color: '#000', borderColor: '#fff' }}>
            Start free trial
          </Link>
        </div>
      </div>

      <p style={{ textAlign: 'center', fontSize: '12px', color: '#64748B', margin: '32px auto 0', maxWidth: '620px' }}>
        Pricing is early-stage and may change as we finalize payer integrations. If it does,
        current customers keep their current rate for 12 months.
      </p>
    </div>
  </section>
);

const FinalCta = () => (
  <section className={styles.final}>
    <div className={styles.container}>
      <h2 className={styles['h-display']} style={{ fontSize: '80px' }}>
        Your next denial doesn't have to take 45 minutes.
      </h2>
      <p className={styles['hero-sub']} style={{ fontSize: '19px' }}>
        Upload one denial letter and generate your first appeal in the next five minutes. Free, no
        credit card, no call with sales.
      </p>
      <div className={`${styles['hero-ctas']} ${styles.ctas}`}>
        <Link
          to="/signup"
          className={`${styles.btn} ${styles.primary}`}
          style={{ height: '56px', padding: '0 28px', fontSize: '16px' }}
        >
          Start free trial <ArrowRight size={16} />
        </Link>
        <a
          href="mailto:hello@meduri.com"
          className={styles.btn}
          style={{ height: '56px', padding: '0 28px', fontSize: '16px' }}
        >
          Book a 15-minute demo
        </a>
      </div>
      <div className={styles['hero-meta']}>
        <span><Check size={14} /> 10 free appeals</span>
        <span><Check size={14} /> No credit card</span>
        <span><Check size={14} /> BAA on request</span>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className={styles.footer}>
    <div className={styles.container}>
      <div className={styles['footer-top']}>
        <div className={styles['footer-brand']}>
          <Link to="/" className={styles['nav-brand']}>
            <span className={styles['nav-mark']}>AB</span>
            <span className={styles['nav-wordmark']}>AppealBot</span>
            <span className={styles['nav-by']}>by Meduri</span>
          </Link>
          <p>AI-assisted denied-claim appeals for small healthcare practices. Built in partnership with physical therapy clinics.</p>
        </div>
        <div>
          <h5>Product</h5>
          <ul>
            <li><a href="#product">Features</a></li>
            <li><a href="#how">How it works</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#">Changelog</a></li>
            <li><a href="#">Roadmap</a></li>
          </ul>
        </div>
        <div>
          <h5>Company</h5>
          <ul>
            <li><a href="#">About Meduri</a></li>
            <li><a href="#">Contact</a></li>
            <li><a href="#">Careers</a></li>
            <li><a href="#">Blog</a></li>
          </ul>
        </div>
        <div>
          <h5>Security &amp; legal</h5>
          <ul>
            <li><Link to="/security">Security</Link></li>
            <li><a href="#">BAA</a></li>
            <li><Link to="/privacy">Privacy policy</Link></li>
            <li><Link to="/terms">Terms of service</Link></li>
            <li><a href="#">Status</a></li>
          </ul>
        </div>
      </div>
      <div className={styles['footer-bottom']}>
        <span>© 2026 Meduri LLC · Made for small practices.</span>
        <div className={styles.legal}>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="#">DPA</a>
        </div>
      </div>
    </div>
  </footer>
);

const LandingPage = () => (
  <div className={styles.landing}>
    <Nav />
    <Hero />
    <PayerRow />
    <ProblemSection />
    <FeaturesSection />
    <HowItWorks />
    <BuiltForSmallPractices />
    <TrustSection />
    <RoiSection />
    <Pricing />
    <FinalCta />
    <Footer />
  </div>
);

export default LandingPage;
